import { useMemo, useState } from 'react';
import { ScheduleListPanel } from '@/pages/schedules/components/schedule-list-panel';
import { RunsFeedPanel } from '@/pages/schedules/components/runs-feed-panel';
import { RunDetailPanel } from '@/pages/schedules/components/run-detail-panel';
import { ScheduleFormModal } from '@/pages/schedules/components/schedule-form-modal';
import { useListScheduledRuns } from '@/hooks/queries/schedules/use-list-scheduled-runs';
import { useListOrgSchedules } from '@/hooks/queries/schedules/use-list-org-schedules';
import { useResizablePanel } from '@/hooks/use-resizable-panel';
import {
  useSchedulesLayoutStore,
  SCHEDULE_LIST_MIN_WIDTH,
  SCHEDULE_LIST_MAX_WIDTH,
  RUNS_FEED_MIN_WIDTH,
  RUNS_FEED_MAX_WIDTH,
} from '@/stores/schedules-layout-store';
import type { OrgSchedule, ScheduledRun } from '@/types/schedules';
import { dayRange } from '@/pages/schedules/helpers/day-range';

export function SchedulesPage() {
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(
    null,
  );
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<OrgSchedule | null>(
    null,
  );
  const [isFormOpen, setIsFormOpen] = useState(false);

  const scheduleListWidth = useSchedulesLayoutStore((s) => s.scheduleListWidth);
  const setScheduleListWidth = useSchedulesLayoutStore(
    (s) => s.setScheduleListWidth,
  );
  const runsFeedWidth = useSchedulesLayoutStore((s) => s.runsFeedWidth);
  const setRunsFeedWidth = useSchedulesLayoutStore((s) => s.setRunsFeedWidth);

  const scheduleListResize = useResizablePanel({
    panelSize: scheduleListWidth,
    setPanelSize: setScheduleListWidth,
    edge: 'right',
    minSize: SCHEDULE_LIST_MIN_WIDTH,
    maxSize: SCHEDULE_LIST_MAX_WIDTH,
  });

  const runsFeedResize = useResizablePanel({
    panelSize: runsFeedWidth,
    setPanelSize: setRunsFeedWidth,
    edge: 'right',
    minSize: RUNS_FEED_MIN_WIDTH,
    maxSize: RUNS_FEED_MAX_WIDTH,
  });

  const range = useMemo(() => dayRange(selectedDate), [selectedDate]);

  const runsParams = useMemo(
    () => ({
      scheduleId: selectedScheduleId ?? undefined,
      dateFrom: range?.from,
      dateTo: range?.to,
    }),
    [selectedScheduleId, range],
  );

  const schedulesQuery = useListOrgSchedules();
  const runsQuery = useListScheduledRuns(runsParams);

  const schedules = useMemo(
    () => schedulesQuery.data ?? [],
    [schedulesQuery.data],
  );

  // The runs endpoint falls back schedule_name to the raw temporal id; prefer
  // the human name from the org-schedules list when we have it.
  const scheduleNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of schedules) {
      if (s.name?.trim()) map.set(s.id, s.name.trim());
    }
    return map;
  }, [schedules]);

  const runs: ScheduledRun[] = useMemo(() => {
    const raw = runsQuery.data?.pages.flatMap((p) => p.runs ?? []) ?? [];
    return raw.map((run) => {
      const resolved = run.schedule_id
        ? scheduleNameById.get(run.schedule_id)
        : undefined;
      return resolved ? { ...run, schedule_name: resolved } : run;
    });
  }, [runsQuery.data, scheduleNameById]);

  const selectedRun: ScheduledRun | null =
    runs.find((r) => r.id === selectedRunId) ?? null;

  const openCreate = () => {
    setEditingSchedule(null);
    setIsFormOpen(true);
  };

  const openEdit = (schedule: OrgSchedule) => {
    setEditingSchedule(schedule);
    setIsFormOpen(true);
  };

  return (
    <div className='flex h-full w-full overflow-hidden'>
      <ScheduleListPanel
        width={scheduleListResize.panelWidth}
        onResizeStart={scheduleListResize.handleResizeStart}
        schedules={schedules}
        isLoading={schedulesQuery.isLoading}
        isError={schedulesQuery.isError}
        selectedScheduleId={selectedScheduleId}
        onSelectSchedule={setSelectedScheduleId}
        selectedDate={selectedDate}
        onSelectDate={setSelectedDate}
        onCreate={openCreate}
        onEdit={openEdit}
        onScheduleDeleted={(id) => {
          if (selectedScheduleId === id) setSelectedScheduleId(null);
        }}
      />
      <RunsFeedPanel
        width={runsFeedResize.panelWidth}
        onResizeStart={runsFeedResize.handleResizeStart}
        runs={runs}
        isLoading={runsQuery.isLoading}
        isError={runsQuery.isError}
        selectedScheduleId={selectedScheduleId}
        schedules={schedules}
        selectedRunId={selectedRunId}
        onSelectRun={setSelectedRunId}
        hasNextPage={runsQuery.hasNextPage}
        isFetchingNextPage={runsQuery.isFetchingNextPage}
        onLoadMore={runsQuery.fetchNextPage}
      />
      <RunDetailPanel run={selectedRun} />
      <ScheduleFormModal
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        schedule={editingSchedule}
      />
    </div>
  );
}
